import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

/**
 * Reset password — mirrors `.old/resources/views/auth/reset-password.blade.php`.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = params.get('email') ?? '';
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const linkBroken = !email || !token;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/v1/auth/reset-password', { email, token, password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError('This reset link is invalid or has expired. Please request a new one.');
      } else if (err instanceof ApiError) {
        setError(`Reset failed (${err.status}).`);
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-body p-4">
          <div className="text-center w-75 m-auto">
            <h4 className="text-dark-50 text-center mt-0 fw-bold">Choose a new password</h4>
            <p className="text-muted mb-4">
              For <strong>{email || '—'}</strong>
            </p>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {linkBroken ? (
            <div className="alert alert-danger">
              This reset link is missing required information.{' '}
              <Link to="/forgot-password">Request a new link</Link>.
            </div>
          ) : done ? (
            <div className="alert alert-success">
              Password updated. Redirecting to sign in…
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate data-disable-unsaved-alert>
              <div className="mb-3">
                <label htmlFor="newpw" className="form-label">New password</label>
                <input
                  type="password"
                  id="newpw"
                  className="form-control"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="confirmpw" className="form-label">Confirm new password</label>
                <input
                  type="password"
                  id="confirmpw"
                  className="form-control"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>

              <div className="mb-0 text-center">
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Reset password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 text-center">
          <p className="text-muted">
            Back to{' '}
            <Link to="/login" className="text-muted ms-1">
              <b>Log In</b>
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
